/**
 * URL linkification for bot messages.
 *
 * Pure + framework-agnostic so it can be unit-tested and reused by the Lit
 * renderer (which turns tokens into <a> template parts — never unsafeHTML, so
 * the surrounding text stays auto-escaped / XSS-safe).
 *
 * Only explicit http(s):// URLs are linked — bare domains like "kobot.chat" are
 * NOT, to avoid linkifying prose ("plik.shop", "wersja2.app", "asystent.AI").
 * Trailing sentence punctuation ("…kobot.chat." / "(…)") is peeled off the link,
 * but a balanced closing bracket (".../Foo_(bar)") is kept.
 *
 * KEEP IN SYNC with the server renderer App\Models\Message (linkify +
 * splitTrailingPunctuation) — same URL pattern + same trailing rules, so the
 * owner transcript renders 1:1 with the widget. tests/format-links.test.ts
 * mirrors MessageRenderedTest.
 */

const URL_RE = /https?:\/\/[^\s<]+/gi;

// Markdown link [label](https://…) — model pisze linki w składni MD; bez tego
// pre-passu bare-URL pass łykał "](https://…)" do środka hrefa i całość
// renderowała się jako zepsuty odnośnik (prod 2026-09-01). Href tylko http(s).
const MD_LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;
const ALWAYS_TRAILING = /[.,;:!?'"«»…]$/u;

export type LinkToken = string | { url: string; href: string };

const count = (s: string, ch: string): number => s.split(ch).length - 1;

/**
 * Peel sentence punctuation off the end of a matched URL. A closing ')'/']' is
 * kept when balanced inside the URL and only stripped when there are more closers
 * than openers (URL written in parentheses). Returns [url, trailing].
 */
function splitTrailing(url: string): [string, string] {
  let trailing = '';

  while (url !== '') {
    if (ALWAYS_TRAILING.test(url)) {
      trailing = url.slice(-1) + trailing;
      url = url.slice(0, -1);
      continue;
    }
    const last = url.slice(-1);
    if (last === ')' && count(url, ')') > count(url, '(')) {
      trailing = ')' + trailing;
      url = url.slice(0, -1);
      continue;
    }
    if (last === ']' && count(url, ']') > count(url, '[')) {
      trailing = ']' + trailing;
      url = url.slice(0, -1);
      continue;
    }
    break;
  }

  return [url, trailing];
}

/** Bare-URL pass (poprzednie linkifyTokens) — działa na runach BEZ linków MD. */
function bareUrlTokens(text: string): LinkToken[] {
  const out: LinkToken[] = [];
  const re = new RegExp(URL_RE.source, 'gi');
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(text)) !== null) {
    const [url, trailing] = splitTrailing(m[0]);
    if (url === '') {
      continue;
    }

    if (m.index > last) {
      out.push(text.slice(last, m.index));
    }
    out.push({ url, href: url });
    if (trailing) {
      out.push(trailing);
    }
    last = re.lastIndex;
  }

  if (last < text.length) {
    out.push(text.slice(last));
  }
  return out;
}

/**
 * Split text into plain strings and link tokens. Linki markdown [label](url)
 * najpierw (wyświetlamy TYLKO label, href = cel; label będący URL-em zostaje
 * tekstem etykiety), potem bare-URL pass na pozostałych runach. Dla tokenów MD
 * reasemblacja NIE odtwarza źródła (składnia [..](..) znika — celowo).
 */
export function linkifyTokens(text: string): LinkToken[] {
  const out: LinkToken[] = [];
  const md = new RegExp(MD_LINK_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = md.exec(text)) !== null) {
    if (m.index > last) {
      out.push(...bareUrlTokens(text.slice(last, m.index)));
    }
    out.push({ url: m[1], href: m[2] });
    last = md.lastIndex;
  }

  if (last < text.length) {
    out.push(...bareUrlTokens(text.slice(last)));
  }
  return out;
}
